/**
 * @element qds-menu-spacer
 * 
 * A vertical line divider for separating menu items or sections in a toolbar.
 * 
 * @attr {number} height - The height of the spacer in pixels (default: 24)
 * @attr {number} width - The width of the spacer line in pixels (default: 1)
 * @attr {number} margin - The horizontal margin on both sides in pixels (default: 8)
 * 
 * @csspart spacer - The spacer container
 * @csspart line - The vertical line element
 */

import { loadBaseCSS } from '../shared/button-utils.js';

// Load base CSS
loadBaseCSS();

export class QdsMenuSpacer extends HTMLElement {
  // Private properties
  private _height: number;
  private _width: number;
  private _margin: number;

  static get observedAttributes(): string[] {
    return ['height', 'width', 'margin'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    
    // Initialize properties with defaults
    this._height = 24;  // Default height in pixels
    this._width = 1;    // Default width in pixels
    this._margin = 8;   // Default margin in pixels
    
    this.render();
  }

  connectedCallback(): void {
    if (!this.isConnected) return;
    // No additional setup needed for now
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    if (oldValue === newValue) return;

    const value = parseInt(newValue, 10);
    if (isNaN(value)) return;

    switch (name) {
      case 'height':
        this._height = value;
        break;
      case 'width':
        this._width = value;
        break;
      case 'margin':
        this._margin = value;
        break;
    }

    this.render();
  }

  // Getters and setters
  get height(): number {
    return this._height;
  }

  set height(value: number) {
    this.setAttribute('height', value.toString());
  }

  get width(): number {
    return this._width;
  }

  set width(value: number) {
    this.setAttribute('width', value.toString());
  }

  get margin(): number {
    return this._margin;
  }

  set margin(value: number) {
    this.setAttribute('margin', value.toString());
  }

  private render(): void {
    if (!this.shadowRoot) return;

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          --_menu-spacer-height: var(--qds-menu-spacer-height, ${this._height}px);
          --_menu-spacer-width: var(--qds-menu-spacer-width, ${this._width}px);
          --_menu-spacer-margin: var(--qds-menu-spacer-margin, ${this._margin}px);
          --_menu-spacer-color: var(--spacer-color, var(--theme-border-strong));
          display: inline-flex;
          align-self: stretch;
        }
        
        .spacer {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          margin: 0 var(--_menu-spacer-margin);
          height: 100%;
        }
        
        .line {
          height: var(--_menu-spacer-height);
          width: var(--_menu-spacer-width);
          background-color: var(--_menu-spacer-color);
          border-radius: var(--theme-radius-sm, 4px);
        }
      </style>
      
      <div class="spacer" part="spacer">
        <div class="line" part="line"></div>
      </div>
    `;
  }
}

// Only define the custom element if it's not already defined
if (!customElements.get('qds-menu-spacer')) {
  customElements.define('qds-menu-spacer', QdsMenuSpacer);
}
